import { useState } from "react";
import { useShedConfig } from "../../contexts/ShedConfigContext";
import InfoTooltip from "../layout/InfoTooltip";
import QuantitySelector from "./QuantitySelector";

const extraTypes = [
  {
    id: "windows" as const,
    name: "Window",
    tooltip: "Provide natural light and ventilation to the interior.",
    image: "/images/extras/window-front.png",
    hasSize: true,
  },
  {
    id: "vents" as const,
    name: "Vent",
    tooltip: "Improve air circulation and help regulate interior temperature.",
    image: "/images/extras/vent-front.png",
  },
];

export default function ExtrasAccordion() {
  const { extras, toggleExtra, setExtraQuantity, setExtraSize } =
    useShedConfig();
  const [isMainOpen, setIsMainOpen] = useState(false);
  const [openExtras, setOpenExtras] = useState<Record<string, boolean>>({});

  const toggleMain = () => setIsMainOpen(!isMainOpen);
  const toggleExtraSection = (id: string) => {
    setOpenExtras((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="gap-3 w-full mt-4">
      <button
        type="button"
        onClick={toggleMain}
        className="flex items-center justify-between w-full p-2 font-medium text-left text-gray-900 border border-gray-200 rounded-xl hover:bg-gray-100 hover:cursor-pointer"
      >
        <span className="ml-4 text-xl">
          Extras
          <InfoTooltip>
            Here you can add additional elements that will be part of your
            structure. Use the (+/-) buttons to indicate how many you want to
            include. These extras will appear reflected in your final report.
          </InfoTooltip>
        </span>
        <svg
          className={`w-3 h-3 shrink-0 transition-transform ${isMainOpen ? "" : "rotate-180"}`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 10 6"
        >
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 5 5 1 1 5"
          />
        </svg>
      </button>

      {isMainOpen && (
        <div className="border border-gray-200 border-t-0 rounded-b-xl">
          {extraTypes.map((extraType) => (
            <div
              key={extraType.id}
              className="border-b border-gray-200 last:border-b-0"
            >
              <button
                type="button"
                onClick={() => toggleExtraSection(extraType.id)}
                className="flex items-center justify-between w-full p-3 font-medium text-left text-gray-700 hover:bg-gray-50 hover:cursor-pointer"
              >
                <span className="ml-4">{extraType.name}</span>
                <svg
                  className={`w-3 h-3 shrink-0 transition-transform ${openExtras[extraType.id] ? "" : "rotate-180"}`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 10 6"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5 5 1 1 5"
                  />
                </svg>
              </button>

              {openExtras[extraType.id] && (
                <div className="p-4 bg-[#fcf8efe1]">
                  <div className="text-center">
                    <input
                      type="checkbox"
                      checked={extras[extraType.id].selected}
                      onChange={() => toggleExtra(extraType.id)}
                      className="mr-2"
                    />
                    {extraType.name}
                    <InfoTooltip>{extraType.tooltip}</InfoTooltip>

                    <img
                      src={extraType.image}
                      alt={extraType.name}
                      className="w-56 items-center mx-auto my-2"
                    />

                    {extraType.hasSize && (
                      <input
                        type="text"
                        value={extras[extraType.id].size || ""}
                        onChange={(e) =>
                          setExtraSize(extraType.id, e.target.value)
                        }
                        className="w-1/2 items-center p-2 border border-gray-300 rounded mb-2"
                        placeholder={`Add ${extraType.name.toLowerCase()} size`}
                      />
                    )}

                    <div className="flex justify-center mb-2">
                      <QuantitySelector
                        value={extras[extraType.id].quantity}
                        onChange={(qty) => setExtraQuantity(extraType.id, qty)}
                        disabled={!extras[extraType.id].selected}
                      />
                    </div>

                    <p className="text-[8px] text-gray-500 ml-2">
                      Use the (+/-) buttons to select how many{" "}
                      {extraType.name.toLowerCase()}s you want to install.
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
